import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Container, Row, Col, Table, Button, Form } from 'react-bootstrap';
import { format } from 'date-fns';

import Spinner from '../../../../components/Spinner';
import Toast from '../../../../components/Toast';
import { authUserHasModuleAccessMany } from "../../../../utils/auth";
import UsersAPI from '../../../../api/Users';
import { TokenActionModal } from './TokenActionModal';

const MANAGE_MODULE_ID = 1000; // "Manage User Tokens" (fixed id, see the back-end migration)

const ACTION_NAMES = {
    grant: "Granted",
    convert: "Converted",
    change_expiry: "Changed expiration",
    void: "Voided",
};

const showDate = (value, empty = "No expiration") => value ? format(new Date(value), "MM/dd/yyyy") : empty;
const isExpired = (token) => !!token.exp_date && new Date(token.exp_date) <= new Date();

const historyDetails = (row) => {
    const from = showDate(row.from_exp_date, "never");
    const to = showDate(row.to_exp_date, "never");
    switch (row.action) {
        case "grant":
            return `${row.to_type}, expires ${to}`;
        case "convert":
            return `${row.from_type} to ${row.to_type}` + (from !== to ? `; expiration ${from} to ${to}` : "");
        case "change_expiry":
            return `Expiration ${from} to ${to}`;
        case "void":
            return row.from_exp_date ? `Expired immediately (was ${from})` : "Expired immediately (had no expiration)";
        default:
            return "";
    }
};

/** Staff view of a user's unused tokens, with grant / convert / change expiry / void and the change history. */
export const Tokens = (props) => {
    const userId = props.user_id;

    const [userHasModulePermission, setUserHasModulePermission] = useState(null);
    const [tokens, setTokens] = useState(null);
    const [types, setTypes] = useState([]);
    // restricted: a service manager, limited to the token types their services use
    const [access, setAccess] = useState({ loaded: false, allowed: false, restricted: false, message: null });
    const [history, setHistory] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [action, setAction] = useState(null);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const checkPermission = async () => {
            try {
                let response = await authUserHasModuleAccessMany([MANAGE_MODULE_ID], userId);
                setUserHasModulePermission(response);
            } catch (ex) { console.error(ex) }
        }
        checkPermission();
    }, [userId]);

    const load = useCallback(async () => {
        try {
            // include_expired 1 = expired and unexpired; redeemed tokens are left out
            const [tokenResponse, historyResponse] = await Promise.all([
                UsersAPI.tokens({ user_id: userId, include_expired: 1 }),
                UsersAPI.tokenHistory({ user_id: userId }),
            ]);
            if (tokenResponse?.errors) setError(tokenResponse.errors);
            setTokens(tokenResponse?.data || []);
            setHistory(historyResponse?.data || []);
        } catch (ex) {
            console.error(ex);
            setError("Could not load this user's tokens.");
            setTokens([]);
        }
    }, [userId]);

    useEffect(() => {
        if (userHasModulePermission?.[MANAGE_MODULE_ID]) {
            load();
            UsersAPI.tokenTypes()
                .then(response => {
                    if (response?.errors) {
                        setTypes([]);
                        setAccess({ loaded: true, allowed: false, restricted: true, message: Array.isArray(response.errors) ? response.errors.join(" ") : String(response.errors) });
                    } else {
                        setTypes(response?.data?.types || []);
                        setAccess({ loaded: true, allowed: true, restricted: !!response?.data?.restricted, message: null });
                    }
                })
                .catch(ex => console.error(ex));
        }
    }, [userHasModulePermission, load]);

    const sortedTokens = useMemo(() => {
        if (!tokens) return [];
        return [...tokens].sort((a, b) => {
            if (isExpired(a) !== isExpired(b)) return isExpired(a) ? 1 : -1;
            if (a.product_name !== b.product_name) return a.product_name.localeCompare(b.product_name);
            return (a.exp_date || "9999").localeCompare(b.exp_date || "9999");
        });
    }, [tokens]);

    // which tokens this user may act on: everything for owners/admins, only their services' types for managers
    const canActOn = useCallback((token) => {
        if (!access.allowed) return false;
        if (!access.restricted) return true;
        return types.some(type => type.variant_id === token.product_variant_id);
    }, [access, types]);

    const actionableTokens = useMemo(() => sortedTokens.filter(canActOn), [sortedTokens, canActOn]);

    const selectedTokens = useMemo(
        () => sortedTokens.filter(token => selectedIds.includes(token.id)),
        [sortedTokens, selectedIds]
    );
    const selectedActive = selectedTokens.filter(token => !isExpired(token));
    const activeCount = sortedTokens.filter(token => !isExpired(token)).length;

    const toggle = (tokenId) => {
        setSelectedIds(prev => prev.includes(tokenId) ? prev.filter(id => id !== tokenId) : [...prev, tokenId]);
    };

    const toggleAll = (checked) => {
        setSelectedIds(checked ? actionableTokens.map(token => token.id) : []);
    };

    const handleDone = (message) => {
        setAction(null);
        setSelectedIds([]);
        setSuccess(<Toast>{message}</Toast>);
        load();
        if (props.triggerUserReset) props.triggerUserReset(); // the profile card's wallet count
    };

    if (userHasModulePermission === null) return <Spinner />;

    if (!userHasModulePermission[MANAGE_MODULE_ID]) {
        return (
            <Container>
                <h4 className="section-title">Tokens</h4>
                <hr />
                <p>You do not have permission to manage this user's tokens.</p>
            </Container>
        );
    }

    if (tokens === null) return (
        <SkeletonTheme color="#e0e0e0">
            <div className="mt-3 text-center">
                <Skeleton height={28} width={200} />
                <Skeleton height={16} count={4} />
            </div>
        </SkeletonTheme>
    );

    return (
        <Container className="user-tokens" data-cy="user-tokens">
            {success}
            <h4 className="section-title">Tokens</h4>
            <hr />
            {error && <p className="text-danger">{Array.isArray(error) ? error.join(" ") : String(error)}</p>}
            {access.loaded && !access.allowed &&
                <p className="text-muted mb-3" data-cy="tokens-read-only">{access.message} You can see this user's tokens but not change them.</p>
            }
            {access.loaded && access.allowed && access.restricted &&
                <p className="text-muted small mb-3" data-cy="tokens-restricted">
                    You can change the token types used by the services you manage: {types.map(type => type.product_name).join(", ")}.
                </p>
            }

            <Row className="mb-3">
                <Col className="d-flex flex-wrap gap-2 align-items-center">
                    <Button onClick={() => setAction("grant")} disabled={!access.allowed || !types.length} data-cy="tokens-grant">Grant tokens</Button>
                    <Button variant="secondary" disabled={!selectedTokens.length} onClick={() => setAction("convert")} data-cy="tokens-convert">Convert</Button>
                    <Button variant="secondary" disabled={!selectedTokens.length} onClick={() => setAction("change_expiry")} data-cy="tokens-expiry">Change expiration</Button>
                    <Button
                        variant="outline-danger"
                        disabled={!selectedActive.length || selectedActive.length !== selectedTokens.length}
                        title={selectedActive.length !== selectedTokens.length ? "Only unexpired tokens can be voided" : undefined}
                        onClick={() => setAction("void")}
                        data-cy="tokens-void"
                    >
                        Void
                    </Button>
                    <span className="text-muted ms-2">
                        {activeCount} active, {sortedTokens.length - activeCount} expired
                        {selectedTokens.length > 0 && ` - ${selectedTokens.length} selected`}
                    </span>
                </Col>
            </Row>

            {sortedTokens.length === 0 ?
                <p>This user has no unused tokens.</p>
            :
                <Table striped hover size="sm" responsive data-cy="tokens-table">
                    <thead>
                        <tr>
                            <th>
                                <Form.Check
                                    type="checkbox"
                                    aria-label="Select all tokens"
                                    disabled={!actionableTokens.length}
                                    checked={actionableTokens.length > 0 && selectedIds.length === actionableTokens.length}
                                    onChange={(e) => toggleAll(e.target.checked)}
                                />
                            </th>
                            <th>Token</th>
                            <th>Expires</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedTokens.map(token => (
                            <tr key={`token-${token.id}`} className={isExpired(token) ? "text-muted" : ""}>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        aria-label={`Select token ${token.id}`}
                                        disabled={!canActOn(token)}
                                        title={!canActOn(token) ? "Not used by a service you manage" : undefined}
                                        checked={selectedIds.includes(token.id)}
                                        onChange={() => toggle(token.id)}
                                    />
                                </td>
                                <td>{token.product_name}</td>
                                <td>{showDate(token.exp_date)}</td>
                                <td>{isExpired(token) ? "Expired" : "Active"}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            }

            <h5 className="mt-4">Change history</h5>
            {history.length === 0 ?
                <p className="text-muted">No staff changes yet.</p>
            :
                <Table size="sm" responsive data-cy="tokens-history">
                    <thead>
                        <tr>
                            <th>When</th>
                            <th>Change</th>
                            <th>Token</th>
                            <th>Details</th>
                            <th>Reason</th>
                            <th>By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.map(row => (
                            <tr key={`token-history-${row.id}`}>
                                <td>{showDate(row.created_at, "")}</td>
                                <td>{ACTION_NAMES[row.action] || row.action}</td>
                                <td>#{row.token_id}</td>
                                <td>{historyDetails(row)}</td>
                                <td>{row.reason}</td>
                                <td>{row.adjusted_by}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            }

            {action &&
                <TokenActionModal
                    show={!!action}
                    action={action}
                    userId={userId}
                    selectedTokens={action === "grant" ? [] : selectedTokens}
                    types={types}
                    onClose={() => setAction(null)}
                    onDone={handleDone}
                />
            }
        </Container>
    );
}

export default Tokens;
